import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Check, Eraser, PencilRuler, ShieldCheck, Image as ImageIcon, FileText } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function SignaturePad({ onChange }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const dirty = useRef(false);
  const pos = (e) => {
    const r = ref.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  };
  const start = (e) => { drawing.current = true; const c = ref.current.getContext("2d"); const p = pos(e); c.beginPath(); c.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const c = ref.current.getContext("2d"); const p = pos(e); c.lineWidth = 2.2; c.lineCap = "round"; c.strokeStyle = "#0f172a"; c.lineTo(p.x, p.y); c.stroke(); dirty.current = true; };
  const end = () => { if (drawing.current && dirty.current) onChange(ref.current.toDataURL("image/png")); drawing.current = false; };
  const clear = () => { const c = ref.current.getContext("2d"); c.clearRect(0, 0, ref.current.width, ref.current.height); dirty.current = false; onChange(null); };
  return (
    <div>
      <canvas ref={ref} width={520} height={170} data-testid="owner-confirm-signature-pad"
        className="w-full border border-slate-200 rounded-lg touch-none bg-white"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
      <button onClick={clear} data-testid="owner-confirm-signature-clear" className="mt-1 text-[12px] text-slate-400 hover:text-red-500 flex items-center gap-1"><Eraser className="h-3 w-3" />清除重签 Clear</button>
    </div>
  );
}

export default function OwnerDesignConfirm() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [sig, setSig] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const fileUrl = (fid) => `${BACKEND}/api/public/design-change/${token}/file/${fid}`;

  useEffect(() => {
    fetch(`${BACKEND}/api/public/design-change/${token}`)
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((d) => { setData(d); setDone(!!d.confirmed); })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [token]);

  const confirm = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND}/api/public/design-change/${token}/confirm`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, signature: sig }),
      });
      if (res.ok) setDone(true);
    } catch (_) {}
    setSubmitting(false);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-400 text-sm">链接无效或已失效 Link invalid or expired</div>;

  const atts = data.attachments || [];
  const isImage = (a) => /^image\//.test(a.contentType || "");

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="owner-design-confirm-page">
      <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-100">
          <PencilRuler className="h-5 w-5 text-red-500" />
          <div>
            <h1 className="text-base font-bold text-slate-800">设计变更确认 Design Change</h1>
            {data.address && <p className="text-[11px] text-slate-400">{data.address}</p>}
          </div>
        </div>

        <div className="px-5 py-4 space-y-3">
          <div>
            <p className="text-sm font-semibold text-slate-800">{data.title}</p>
            {data.changeNo && <p className="text-[11px] font-mono text-slate-400 mt-0.5">NO. {data.changeNo}</p>}
            {data.description && <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{data.description}</p>}
            {data.designer && <p className="text-[11px] text-slate-400 mt-1">建筑设计师 Architect: {data.designer}</p>}
          </div>

          {atts.length > 0 && (
            <div className="grid grid-cols-3 gap-2" data-testid="owner-confirm-attachments">
              {atts.map((a) => (
                <a key={a.fileId} href={fileUrl(a.fileId)} target="_blank" rel="noreferrer" className="block">
                  {isImage(a)
                    ? <img src={fileUrl(a.fileId)} alt={a.filename} className="h-24 w-full object-cover rounded-lg border border-slate-200" />
                    : <div className="h-24 w-full flex flex-col items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-slate-400"><FileText className="h-6 w-6" /><span className="text-[9px] truncate max-w-full px-1">{a.filename}</span></div>}
                </a>
              ))}
            </div>
          )}

          {done ? (
            <div className="flex flex-col items-center py-6 text-center" data-testid="owner-confirm-done">
              <ShieldCheck className="h-10 w-10 text-green-500" />
              <p className="text-sm font-semibold text-slate-700 mt-2">您已确认此设计变更</p>
              <p className="text-[11px] text-slate-400 mt-1">Thank you. This change has been confirmed.</p>
            </div>
          ) : (
            <div className="space-y-3 pt-1">
              <div>
                <label className="text-[12px] font-medium text-slate-600">您的姓名 Your Name</label>
                <input data-testid="owner-confirm-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="业主姓名" className="mt-1 w-full text-sm rounded-md border border-slate-200 px-2.5 py-2 focus:outline-none focus:ring-1 focus:ring-brand-400" />
              </div>
              <div>
                <p className="text-[12px] font-medium text-slate-600 mb-1">签名确认 Signature (可选 Optional)</p>
                <SignaturePad onChange={setSig} />
              </div>
              <button data-testid="owner-confirm-submit" onClick={confirm} disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-1.5 bg-brand-500 hover:bg-brand-600 text-white text-sm font-semibold rounded-lg py-2.5 transition-colors">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}确认此变更 Confirm Change
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
